/**
 * The shapes the Dale dot can take. Every form is the same number of points
 * in the same order, so the orb morphs from one to another by moving each
 * point, never by adding or dropping any. Points are packed x, y, z.
 *
 * - lattice: an even Fibonacci net, crisp like a wireframe (Mercurio).
 * - stipple: a random dusting, shaded by density like a pencil drawing (Venus).
 * - rings: bands of latitude, an abacus of dots (Júpiter's cloud bands).
 * - orbit: the sphere with a ring of dots circling it (Marte and its moons).
 */

export const ORB_FORMS = ['lattice', 'stipple', 'rings', 'orbit'] as const;
export type OrbForm = (typeof ORB_FORMS)[number];

const GOLDEN_ANGLE = Math.PI * (3 - Math.sqrt(5));
const MIN_DOTS = 90;
const MAX_DOTS = 1500;
/** Dots per square pixel of orb: dense enough to read as a surface, sparse enough to stay dots. */
const DOTS_PER_PX2 = 0.034;
const RING_COUNT = 13;
const ORBIT_SHARE = 0.26;
const ORBIT_RADIUS = 1.24;
const ORBIT_TILT = 0.5;
const ORBIT_LEAN = -0.32;

/** How much each form thins its lit side into sparse dots (1) or keeps every dot and shades by tone (0). */
export const ORB_STIPPLE: Readonly<Record<OrbForm, number>> = {
  lattice: 0.3,
  stipple: 1,
  rings: 0.15,
  orbit: 0.35,
};

/** Mulberry32: tiny, fast and repeatable, so an orb looks the same on every visit. */
export function seededRandom(seed: number): () => number {
  let state = seed >>> 0;
  return () => {
    state = (state + 0x6d2b79f5) >>> 0;
    let t = state;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** How many dots an orb of `sizePx` draws. */
export function orbDotCount(sizePx: number): number {
  return Math.round(Math.min(MAX_DOTS, Math.max(MIN_DOTS, sizePx * sizePx * DOTS_PER_PX2)));
}

export function buildOrbForm(form: OrbForm, count: number, seed: number): Float32Array {
  const random = seededRandom(seed);
  if (form === 'stipple') return fill(count, () => randomOnSphere(random));
  if (form === 'rings') return fill(count, (index) => onRing(index, count, random));
  if (form === 'orbit') return fill(count, (index) => orbitPoint(index, count, random));
  return fill(count, (index) => latticePoint(index, count));
}

type Point = readonly [number, number, number];

function fill(count: number, pointAt: (index: number) => Point): Float32Array {
  const points = new Float32Array(count * 3);
  for (let index = 0; index < count; index += 1) {
    points.set(pointAt(index), index * 3);
  }
  return points;
}

function latticePoint(index: number, count: number): Point {
  const y = 1 - (2 * (index + 0.5)) / count;
  const ring = Math.sqrt(1 - y * y);
  const angle = index * GOLDEN_ANGLE;
  return [Math.cos(angle) * ring, y, Math.sin(angle) * ring];
}

function randomOnSphere(random: () => number): Point {
  const y = random() * 2 - 1;
  const ring = Math.sqrt(1 - y * y);
  const angle = random() * Math.PI * 2;
  return [Math.cos(angle) * ring, y, Math.sin(angle) * ring];
}

/** Bands spaced evenly by latitude; each holds dots in step with its length, so the poles do not crowd. */
function onRing(index: number, count: number, random: () => number): Point {
  const band = pickBand(random());
  const latitude = -Math.PI / 2 + ((band + 0.5) * Math.PI) / RING_COUNT;
  const y = Math.sin(latitude);
  const ring = Math.cos(latitude);
  const angle = (index / count) * Math.PI * 2 * 37 + random() * 0.08;
  return [Math.cos(angle) * ring, y, Math.sin(angle) * ring];
}

const BAND_WEIGHTS = Array.from({ length: RING_COUNT }, (_, band) => Math.cos(-Math.PI / 2 + ((band + 0.5) * Math.PI) / RING_COUNT));
const BAND_TOTAL = BAND_WEIGHTS.reduce((sum, weight) => sum + weight, 0);

function pickBand(roll: number): number {
  let remaining = roll * BAND_TOTAL;
  const band = BAND_WEIGHTS.findIndex((weight) => {
    remaining -= weight;
    return remaining < 0;
  });
  return band === -1 ? RING_COUNT - 1 : band;
}

function orbitPoint(index: number, count: number, random: () => number): Point {
  const orbiting = Math.floor(count * ORBIT_SHARE);
  if (index >= orbiting) return latticePoint(index - orbiting, count - orbiting);
  const angle = (index / orbiting) * Math.PI * 2;
  const wobble = 1 + (random() - 0.5) * 0.05;
  const flatX = Math.cos(angle) * ORBIT_RADIUS * wobble;
  const flatZ = Math.sin(angle) * ORBIT_RADIUS * wobble;
  // Tipped toward the viewer and leaning to one side, so it reads as a ring and never as a line.
  const y = flatZ * Math.sin(ORBIT_TILT);
  const z = flatZ * Math.cos(ORBIT_TILT);
  return [flatX * Math.cos(ORBIT_LEAN) - y * Math.sin(ORBIT_LEAN), flatX * Math.sin(ORBIT_LEAN) + y * Math.cos(ORBIT_LEAN), z];
}
